import type { JSX } from 'react';
import { LuBot, LuUsers, LuVideo } from 'react-icons/lu';

import { EventSource, type EventView } from '@ndvr/customer-api';

import { ClockIcon } from '@/components/Icons';
import { convertBackendTimeToBrowser, titleCase } from 'utils';
import { Tag } from 'widgets';

import { EventMeetingBot, getParticipantDisplayNames, getStatusTagType } from './event-details-shared';
import { EventDetailsHouseholdSection } from './EventDetailsHouseholdSection';

interface EventDetailsMetaProps {
    event: EventView;
    eventDate: string;
    eventTimeRange: string;
    meetingBot: EventMeetingBot | null;
}

export function EventDetailsMeta({ event, eventDate, eventTimeRange, meetingBot }: EventDetailsMetaProps): JSX.Element {
    const participantNames = getParticipantDisplayNames(event.participants);
    const isPastEvent = convertBackendTimeToBrowser(event.endTime, event.endTimeZone) < new Date();
    const isHouseholdReadOnly = isPastEvent && Boolean(event.clientHouseholdGUID);

    return (
        <div className="grid gap-3">
            <div className="flex items-center gap-3">
                <ClockIcon className="text-muted-foreground h-4 w-4" />
                <div>
                    <div className="font-medium">{eventDate}</div>
                    <div className="text-muted-foreground text-sm">{eventTimeRange}</div>
                </div>
            </div>

            {event.url ? (
                <div className="flex items-center gap-3">
                    <LuVideo className="text-muted-foreground h-4 w-4" />
                    <span className="text-sm">{titleCase(event.provider)}</span>
                </div>
            ) : null}

            {meetingBot ? (
                <div className="flex items-center gap-3">
                    <LuBot className="text-muted-foreground h-4 w-4" />
                    <Tag type={getStatusTagType(meetingBot.status)}>{meetingBot.status.replace(/_/g, ' ')}</Tag>
                </div>
            ) : null}

            {event.source !== EventSource.LOCAL ? (
                <EventDetailsHouseholdSection event={event} readOnly={isHouseholdReadOnly} />
            ) : null}

            {event.participants?.length ? (
                <div className="flex items-start gap-3">
                    <LuUsers className="text-muted-foreground mt-0.5 h-4 w-4" />
                    <div className="text-sm">
                        <div className="mb-1 font-medium">Attendees</div>
                        <div className="text-muted-foreground">{participantNames}</div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
